<template>
  <div style="background-color: white">
    <div class="full-width-section">
      <section aria-label="Introduction" class="section-padding text-center">
        <h1
          class="mt-16 mx-auto mb-10 alt-h1 font-weight-black text-center"
          v-html="$t('schoolsAndDistricts.introduction.heading')"
        ></h1>

        <p class="mb-8 wider-section mx-auto"></p>
        <v-btn
          class="mx-2 mb-4"
          color="primary"
          large
          href="https://calendly.com/elementari/help"
          target="_blank"
        >
          {{ $t('schoolsAndDistricts.introduction.cta.scheduleDemo') }}
        </v-btn>
        <v-btn
          class="mx-2 mb-4"
          color="secondary"
          :to="localePath('/quote/')"
          outlined
          large
        >
          {{ $t('schoolsAndDistricts.introduction.cta.requestQuote') }}
        </v-btn>
      </section>
    </div>

    <!-- Introduction Content -->
    <div class="full-width-section">
      <v-container class="wider-section">
        <section>
          <p>{{ $t('schoolsAndDistricts.introduction.content.paragraph1') }}</p>
          <p>{{ $t('schoolsAndDistricts.introduction.content.paragraph2') }}</p>
        </section>
      </v-container>
    </div>

    <div class="full-width-section">
      <v-container class="wider-section section-padding">
        <section>
          <h2
            class="font-weight-black mb-12 text-center"
            v-html="$t('schoolsAndDistricts.teachConfidence.heading')"
          ></h2>

          <v-row class="mb-8">
            <!-- Standards-Aligned Interactive Lessons -->
            <v-col cols="12" md="6">
              <div class="d-flex align-start">
                <v-icon class="text-primary mr-4" size="36">
                  {{ icons.mdiBookOpenPageVariant }}
                </v-icon>
                <div>
                  <h3 class="text-h6 font-weight-bold mb-2">
                    {{
                      $t(
                        'schoolsAndDistricts.teachConfidence.features.interactiveLessons.title'
                      )
                    }}
                  </h3>
                  <p class="mb-0">
                    {{
                      $t(
                        'schoolsAndDistricts.teachConfidence.features.interactiveLessons.description'
                      )
                    }}
                  </p>
                </div>
              </div>
            </v-col>

            <!-- Blend Writing and Technology -->
            <v-col cols="12" md="6">
              <div class="d-flex align-start">
                <v-icon class="text-secondary mr-4" size="36">
                  {{ icons.mdiCodeTags }}
                </v-icon>
                <div>
                  <h3 class="text-h6 font-weight-bold mb-2">
                    {{
                      $t(
                        'schoolsAndDistricts.teachConfidence.features.blendWritingTech.title'
                      )
                    }}
                  </h3>
                  <p class="mb-0">
                    {{
                      $t(
                        'schoolsAndDistricts.teachConfidence.features.blendWritingTech.description'
                      )
                    }}
                  </p>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-row class="mb-8">
            <!-- Seamless Classroom Integration -->
            <v-col cols="12" md="6">
              <div class="d-flex align-start">
                <v-icon class="text-success mr-4" size="36">
                  {{ icons.mdiSchool }}
                </v-icon>
                <div>
                  <h3 class="text-h6 font-weight-bold mb-2">
                    {{
                      $t(
                        'schoolsAndDistricts.teachConfidence.features.classroomIntegration.title'
                      )
                    }}
                  </h3>
                  <p class="mb-0">
                    {{
                      $t(
                        'schoolsAndDistricts.teachConfidence.features.classroomIntegration.description'
                      )
                    }}
                  </p>
                </div>
              </div>
            </v-col>

            <!-- Secure and Trusted -->
            <v-col cols="12" md="6">
              <div class="d-flex align-start">
                <v-icon class="text-info mr-4" size="36">
                  {{ icons.mdiShieldCheck }}
                </v-icon>
                <div>
                  <h3 class="text-h6 font-weight-bold mb-2">
                    {{
                      $t(
                        'schoolsAndDistricts.teachConfidence.features.secureTrusted.title'
                      )
                    }}
                  </h3>
                  <p class="mb-0">
                    {{
                      $t(
                        'schoolsAndDistricts.teachConfidence.features.secureTrusted.description'
                      )
                    }}
                  </p>
                </div>
              </div>
            </v-col>
          </v-row>

          <p
            class="mb-0"
            v-html="$t('schoolsAndDistricts.teachConfidence.gettingStarted')"
          ></p>
        </section>
      </v-container>
    </div>

    <!-- SIMPLE CTA -->
    <section class="container wider-section mx-auto">
      <SimpleCTASection
        :headerText="$t('schoolsAndDistricts.callToAction.header')"
        :subheaderText="$t('schoolsAndDistricts.callToAction.subheader')"
        :buttonText="$t('schoolsAndDistricts.callToAction.buttonText')"
        buttonColor="accent"
        :target="localePath('/quote/')"
      />
    </section>

    <div class="full-width-section">
      <v-container class="wider-section section-padding">
        <section>
          <h2
            class="font-weight-black mb-12"
            v-html="$t('schoolsAndDistricts.storytellingCoding.heading')"
          ></h2>
          <p class="mb-8">
            {{ $t('schoolsAndDistricts.storytellingCoding.intro') }}
          </p>

          <!-- Row 1: Text + Video -->
          <v-row align="center" class="mb-8 flex-column-reverse flex-md-row">
            <v-col cols="12" md="6">
              <h3 class="text-h6 font-weight-bold mb-2">
                {{
                  $t(
                    'schoolsAndDistricts.storytellingCoding.sections.improveLiteracy.title'
                  )
                }}
              </h3>
              <p>
                {{
                  $t(
                    'schoolsAndDistricts.storytellingCoding.sections.improveLiteracy.description'
                  )
                }}
              </p>
              <p>
                <!-- <nuxt-link
                  :to="localePath('/why-creative-coding/')"
                  class="internal-link"
                >
                  {{
                    $t(
                      'schoolsAndDistricts.storytellingCoding.sections.improveLiteracy.linkText'
                    )
                  }}
                  <v-icon size="18" color="primary" class="link-icon">
                    {{ icons.mdiArrowRight }}
                  </v-icon>
                </nuxt-link> -->
              </p>
            </v-col>
            <v-col cols="12" md="6" class="text-center">
              <video muted max-width="400" autoplay loop>
                <source
                  src="/img/website/educator/easy-to-use.mp4"
                  type="video/mp4"
                />
                {{ $t('schoolsAndDistricts.storytellingCoding.video.alt') }}
              </video>
            </v-col>
          </v-row>

          <!-- Row 2: Image + Text -->
          <v-row align="center" class="mb-8">
            <v-col
              cols="12"
              md="6"
              class="text-center px-2 py-3 d-flex justify-center"
            >
              <nuxt-img
                placeholder
                format="webp"
                sizes="xs:320px sm:50vw md:80vw lg:500px"
                src="/img/website/resources/7th-grade-coding.jpg"
                :alt="$t('schoolsAndDistricts.storytellingCoding.image.alt')"
                width="400"
                class="mx-auto"
                style="
                  border-radius: 6px;
                  object-fit: cover;
                  max-width: 400px;
                  width: 100%;
                "
              ></nuxt-img>
            </v-col>
            <v-col cols="12" md="6">
              <h3 class="text-h6 font-weight-bold mb-2">
                {{
                  $t(
                    'schoolsAndDistricts.storytellingCoding.sections.learnToCode.title'
                  )
                }}
              </h3>
              <p>
                {{
                  $t(
                    'schoolsAndDistricts.storytellingCoding.sections.learnToCode.description'
                  )
                }}
              </p>
              <p>
                <a href="/why-creative-coding/" class="internal-link">
                  {{
                    $t(
                      'schoolsAndDistricts.storytellingCoding.sections.learnToCode.linkText'
                    )
                  }}
                  <v-icon size="18" color="primary" class="link-icon">
                    {{ icons.mdiArrowRight }}
                  </v-icon>
                </a>
              </p>
            </v-col>
          </v-row>
        </section>
      </v-container>
    </div>

    <div class="full-width-section">
      <section>
        <h2
          class="font-weight-black mb-12 text-center"
          v-html="$t('schoolsAndDistricts.educatorReviews.heading')"
        ></h2>

        <!-- Splide container -->
        <div id="splide" class="splide">
          <div class="splide__track">
            <ul class="splide__list">
              <!-- Review Slides -->
              <li
                class="splide__slide"
                v-for="(review, index) in $t(
                  'schoolsAndDistricts.educatorReviews.reviews'
                )"
                :key="index"
              >
                <div class="review-box pa-4 pa-sm-8">
                  <!-- Video Review -->
                  <div v-if="review.type === 'video'" class="video-wrapper">
                    <iframe
                      src="https://www.youtube.com/embed/_L1SjKWATkI"
                      title="Elementari Testimonial"
                      frameborder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowfullscreen
                      style="width:100%"
                    ></iframe>
                  </div>

                  <!-- Quote Review -->
                  <div v-else>
                    <p class="mb-4 font-weight-normal text-body-1 text-sm-h5">{{ review.text }}</p>
                    <ReviewTeacher
                      class="mb-4"
                      :name="review.name"
                      :position="review.position"
                      :avatarSrc="review.avatarSrc"
                      :avatarSize="$vuetify.breakpoint.smAndDown ? 60 : 90"
                    />
                    <v-btn
                      v-if="review.buttonText"
                      color="secondary"
                      :href="review.buttonLink"
                      target="_blank"
                    >
                      {{ review.buttonText }}
                    </v-btn>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>

    <div class="full-width-section">
      <v-container class="wider-section section-padding">
        <section>
          <h2
            class="font-weight-black mb-12 text-center"
            v-html="$t('schoolsAndDistricts.bringToSchool.heading')"
          ></h2>

          <p class="mb-8">
            {{ $t('schoolsAndDistricts.bringToSchool.intro') }}
          </p>

          <!-- Flexible Plans Details -->
          <v-row align="center" class="mb-8">
            <!-- Image Section -->
            <v-col cols="12" sm="6" class="px-2 py-3 d-flex justify-center">
              <nuxt-img
                placeholder
                format="webp"
                src="/img/website/resources/teacher.png"
                :alt="$t('schoolsAndDistricts.bringToSchool.imageAlt')"
                sizes="xs:320px sm:50vw md:50vw lg:500px"
                class="text-center mx-auto"
                style="
                  border-radius: 6px;
                  object-fit: cover;
                  max-width: 100%;
                  width: 100%;
                "
              ></nuxt-img>
            </v-col>

            <!-- Benefits Section -->
            <v-col cols="12" md="6">
              <ul class="custom-list">
                <li
                  v-html="
                    $t('schoolsAndDistricts.bringToSchool.details.customPlans')
                  "
                ></li>
                <li
                  v-html="
                    $t('schoolsAndDistricts.bringToSchool.details.freePD')
                  "
                ></li>
                <li
                  v-html="
                    $t(
                      'schoolsAndDistricts.bringToSchool.details.easyOnboarding'
                    )
                  "
                ></li>
              </ul>
            </v-col>
          </v-row>

          <p v-html="$t('schoolsAndDistricts.bringToSchool.pricing')"></p>
        </section>
      </v-container>
    </div>

    <div class="full-width-section">
      <v-container class="wider-section section-padding">
        <section>
          <h2
            class="font-weight-black mb-12 text-center"
            v-html="$t('schoolsAndDistricts.studentPrivacy.heading')"
          ></h2>

          <v-row align="center" class="mb-8 flex-column-reverse flex-md-row">
            <!-- Privacy Text -->
            <v-col cols="12" md="6">
              <p
                v-html="
                  $t('schoolsAndDistricts.studentPrivacy.text.description')
                "
              ></p>
              <p
                v-html="
                  $t('schoolsAndDistricts.studentPrivacy.text.privacyPolicy')
                "
              ></p>
            </v-col>

            <!-- Privacy Badge Placeholder -->
            <v-col cols="12" sm="6" class="px-2 py-3 d-flex justify-center">
              <nuxt-img
                placeholder
                format="webp"
                src="/img/website/resources/privacy.png"
                :alt="$t('schoolsAndDistricts.studentPrivacy.image.alt')"
                sizes="xs:320px sm:50vw md:50vw lg:500px"
                style="border-radius: 6px; object-fit: cover; max-width: 100%; width: 100%;"
                class="mx-auto"
              ></nuxt-img>
            </v-col>
          </v-row>
        </section>
      </v-container>
    </div>

    <div class="full-width-section">
      <section aria-label="Call to Action" class="section-padding text-center">
        <h2
          class="font-weight-black mb-12"
          v-html="$t('schoolsAndDistricts.callToAction.heading')"
        ></h2>
        <p class="font-weight-medium mb-8 wider-section mx-auto">
          {{ $t('schoolsAndDistricts.callToAction.description') }}
        </p>
        <v-btn color="accent" :to="localePath('/quote/')" large>
          {{ $t('schoolsAndDistricts.callToAction.buttonText') }}
        </v-btn>
      </section>
    </div>
  </div>
</template>

<script>
  import '@splidejs/splide/dist/css/splide.min.css'
  import Splide from '@splidejs/splide'

  import {
    mdiBookOpenPageVariant,
    mdiCodeTags,
    mdiSchool,
    mdiShieldCheck,
    mdiArrowRight
  } from '@mdi/js'
  export default {
    layout: 'article',
    components: {
      'call-to-action-teacher': () =>
        import('~/components/utils/CallToActionTeacher.vue'),
      'main-footer': () => import('~/components/MainFooter.vue'),
      SimpleCTASection: () =>
        import('~/components/articles/SimpleCTASection.vue'),

      KeyTakeaway: () => import('~/components/articles/KeyTakeaway.vue'),
      SimpleQuote: () => import('~/components/articles/SimpleQuote.vue'),
      LargeQuote: () => import('~/components/articles/LargeQuote.vue'),
      ReviewTeacher: () => import('~/components/utils/ReviewTeacher.vue')
    },
    head() {
      return {
        title: this.$t('meta.schoolsAndDistricts.title'),
        meta: [
          // hid is used as unique identifier. Do not use `vmid` for it as it will not work
          {
            hid: 'description',
            name: 'description',
            content: this.$t('meta.schoolsAndDistricts.description')
          },

          // Open Graph
          {
            hid: 'og:title',
            name: 'og:title',
            content: this.$t('meta.schoolsAndDistricts.title')
          },
          {
            hid: 'og:description',
            name: 'og:description',
            content: this.$t('meta.schoolsAndDistricts.description')
          },
          {
            hid: 'og:url',
            property: 'og:url',
            content: 'https://elementari.com' + this.$route.path
          }
        ]
      }
    },
    data() {
      return {
        icons: {
          mdiBookOpenPageVariant,
          mdiCodeTags,
          mdiSchool,
          mdiShieldCheck,
          mdiArrowRight
        }
      }
    },
    mounted() {
      // Initialize Splide carousel
      new Splide('#splide', {
        type: 'loop',
        perPage: 4,
        padding: '2rem',
        gap: '1rem',
        autoplay: true,
        pauseOnHover: true,
        interval: 5000,
        height: '400px', // Allow height to adjust dynamically
        focus: 'center', // Centers the slide
        arrows: true, // Enable arrows
        pagination: true, // Enable dots
        breakpoints: {
          2000: { perPage: 3, padding: '3rem' },
          1400: { perPage: 2, padding: '2rem' },
          900: { perPage: 1, padding: '1rem', gap: '.5rem' }
        }
      }).mount()
    }
  }
</script>

<style scoped>
  .wider-section {
    max-width: 1100px;
    margin: auto;
  }

  .gradient-text-blue-purple {
    background: linear-gradient(90.91deg, #027bfd -24.67%, #9f51fb 53.55%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }


  /* Button Hover Effects */
  v-btn.large:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  .custom-list {
    list-style-type: disc;
    padding-left: 20px;
    margin: 20px 0;
  }

  .custom-list li {
    margin-bottom: 12px;
  }

  .review-avatar {
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .splide {
    visibility: visible !important;
  }

  .splide__pagination__page {
    width: 12px;
    height: 12px;
    background-color: rgba(0, 0, 0, 0.5); /* Default color */
    border-radius: 50%;
  }

  .splide__pagination__page.is-active {
    background-color: rgba(0, 0, 0, 0.9); /* Highlight active dot */
    transform: scale(1.2); /* Enlarge active dot */
  }

  .review-box {
    border: 4px solid #ff7e5f;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .review-box .review-position {
    margin-top: auto; /* Push position text to the bottom */
  }
  .video-wrapper {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    /* Aspect ratio 16:9 */
    height: 0;
    overflow: hidden;
  }

  .video-wrapper iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
</style>
